import { assign, createMachine, send, actions as xstateActions } from 'xstate';

import {
  COUPON_CODE,
  ERROR,
  getAPIErrorResponse,
  IDLE,
  INVITE_CODE,
  LOADING,
  SUCCESS
} from '@nw/shared/utils';

import { ApplicationSectionNameEnums } from '../../constants/ApplicationSectionConstants';
import {
  NIAT_APPLICATION_MACHINE_ID,
  niatApplicationMachineActions,
  niatApplicationMachineEvents,
  niatApplicationMachineGuards,
  niatApplicationMachineServices,
  niatApplicationMachineStates
} from '../../constants/niatApplicationMachineConstants';
import {
  NiatApplicationMachineContextType,
  NiatApplicationMachineEventTypes
} from '../../types/niatApplicationMachineTypes';

const states = niatApplicationMachineStates;
const events = niatApplicationMachineEvents;
const services = niatApplicationMachineServices;
const guards = niatApplicationMachineGuards;
const actions = niatApplicationMachineActions;

const urlParams = new URLSearchParams(window.location.search);

export const NiatApplicationMachine = createMachine<
  NiatApplicationMachineContextType,
  NiatApplicationMachineEventTypes
>({
  id: NIAT_APPLICATION_MACHINE_ID,
  context: {
    authCode: '',
    loggedInUserRole: '',
    errorMessages: {
      referralCodeErrorMessage: '',
      createPaymentOrderAndGetDetailsErrorMessage: '',
      couponCodeErrorMessage: '',
      submitCampusVisitSchedulerProductErrorMessage: '',
      getInitialFormConfigErrorMessage: '',
      getIbFormSubmissionDetailsErrorMessage: '',
      applicationFormSubmitErrorMessage: '',
      updateUserApplicationApiErrorMessage: '',
      getUserProgramInstituteCoursesErrorMessage: '',
      getSelectedInstituteCoursesErrorMessage: '',
      updateCouponExpiryErrorMessage: ''
    },
    studentLoginDetails: {
      accessToken: '',
      refreshToken: ''
    },
    parentLoginDetails: {
      accessToken: '',
      refreshToken: ''
    },
    userApplicationDetails: null,
    applicationSectionDetailModel: null,
    authMachineRef: null,
    userGraphQlProfileDetails: null,
    isUserApplicationNotExit: false,
    shouldUpdateUserProfileDetailsFlowField: false,
    isGetOtpButtonLoading: false,
    isProceedButtonLoading: false,
    isOtpSentToUser: false,
    userGraphQlIntermediateDetails: null,
    sectionDetailsInfoTabsConfig: null,
    currentSelectedSectionTabId: '',
    currentSelectedSubSectionId: '',
    currentSelectedSectionCompletionPercentage: 0,
    values: {},
    isLoggedIn: false,
    isEmailOtpSentToUser: false,
    isGetEmailOtpButtonLoading: false,
    fieldsGraphQlConfig: {},
    initialValues: null,
    fieldsAuthMachineRefDetails: {},
    studentProfileDetail: null,
    parentProfileDetail: null,
    formSectionConfigModel: null,
    graphQlApiStatus: {
      getUserProfileAndIntermediateDetailGraphQlApiStatus: IDLE,
      getInitialValueOfAllFieldsAPIStatus: IDLE,
      getUserApplicationDetailsAPIStatus: IDLE,
      getReferralCodeDetailApiStatus: IDLE,
      getCouponCodeDetailApiStatus: IDLE,
      createPaymentOrderAndGetDetailsApiStatus: IDLE,
      getInitialFormConfigAPIStatus: IDLE,
      getIbFormSubmissionDetailsAPIStatus: IDLE,
      submitCampusVisitSchedulerProductApiStatus: IDLE,
      updateMultipleGraphQlMutationsStatus: IDLE,
      updateUserApplicationApiStatus: IDLE,
      updateFirstVisitDatetimeApiStatus: {},
      getTopinExamAuthCodeAPIStatus: IDLE,
      getAvailableProgramInstitutesApiStatus: IDLE,
      createUserProgramInstituteApiStatus: IDLE,
      getUserProgramInstituteCoursesApiStatus: IDLE,
      getUserOrdersDetailsApiStatus: IDLE,
      getSelectedInstituteCoursesApiStatus: IDLE
    },
    isUserProfileEmailVerified: false,
    currentAuthenticationField: '',
    shouldShowSuccessPage: false,
    isBootcampRegistrationEditMode: false,
    applicationProductInfo: null,
    paymentServiceInfo: null,
    paymentOrderDetails: null,
    inviteCode: urlParams.get(INVITE_CODE) || '',
    couponCode: urlParams.get(COUPON_CODE) || '',
    paymentAuthCode: '',
    formsAuthCode: '',
    fieldsTemplateResponseConfig: {},
    sectionTemplateResponseConfig: {},
    templateFieldValuesByFieldId: {},
    templatesToGetInitialValues: [],
    currentSectionToUpdateField: '',
    currentSectionToUpdateCompletionPercentage: 0,
    updateFieldFailureErrorMessages: {},
    stateOfResidence: '',
    fieldsAuthDetails: {},
    campusVisitProductConfig: null,
    productsSectionMachineRefDetails: {},
    formSubmissionDetails: null,
    bootcampRegistrationFormSubmissionDetails: null,
    requestObjectArrayForTemplateResponse: [],
    disableFieldValues: {},
    shouldDisableAllSectionsTabs: false,
    isRescheduleExam: false,
    fieldUpdateDetailsQueue: [],
    isFieldUpdateInProgress: false,
    examFeeSectionNameEnum: ApplicationSectionNameEnums.BOOK_ADMISSION_TEST_FEE,
    currentSectionType: '',
    discountDetails: null,
    isDiscountPopupVisible: false,
    isTimerDiscountPopupVisible: false,
    isCouponTimerBannerVisible: false,
    couponTimerBannerTextDetails: null,
    isGracePeriodPopupVisible: false,
    updateCouponExpiryApiStatus: IDLE,
    hasRecordedCopyInteraction: false,
    showCongratulationsModal: true,
    formReferralCodes: null,
    userApiInviteCode: '',
    seatReservationFlow: null,
    topinExamRedirection: {
      url: '',
      windowTarget: ''
    },
    isGetTopinExamAuthCodeLoading: false,
    availableProgramInstitutesData: null,
    seatReservationInstituteSectionDetails: {
      preferredUniversity: '',
      accommodationMode: ''
    },
    latestUserProgramInstituteCourseRow: null,
    selectedInstituteData: null,
    userOrdersDetails: null,
    userEnrollMentStage: null,
    enrollmentCourseIdForPayment: '',
    pendingEnrollmentPaidCrmPayload: null
  },
  initial: states.fetchUserProfileDetailsGraphQlState,
  on: {
    [events.updateLoggedInUserRole]: {
      actions: [actions.updateLoggedInUserRole]
    },
    [events.updateUserProfileDetailGraphQl]: {
      target: states.updateUserProfileDetailGraphQlState
    },

    [events.updateUserIntermediateDetailGraphQl]: {
      target: states.updateUserIntermediateDetailGraphQlState
    },
    [events.updateUserDegreeDetailsGraphQl]: {
      target: states.updateUserDegreeDetailsGraphQlState
    },
    [events.onEntryGetOTP]: {
      actions: [
        actions.setIsGetOtpButtonLoadingToTrue,
        actions.onEntryGetPhoneNumberOtp
      ]
    },
    [events.onEntryGetEmailOtp]: {
      actions: [
        actions.setIsGetEmailOtpButtonLoadingToTrue,
        actions.onEntryGetEmailOtp
      ]
    },
    [events.onSuccessGetOTP]: {
      actions: [
        actions.setIsGetOtpButtonLoadingToFalse,
        assign((context) => {
          return {
            isOtpSentToUser: true,
            fieldsAuthDetails: {
              ...context.fieldsAuthDetails,
              [context.currentAuthenticationField]: {
                isOtpSentToUser: true
              }
            }
          };
        })
      ]
    },
    [events.onSuccessGetEmailOTP]: {
      actions: [
        actions.setIsGetEmailOtpButtonLoadingToFalse,
        assign((context) => {
          return {
            isEmailOtpSentToUser: true
          };
        })
      ]
    },
    [events.onSuccessEmailOtpVerification]: {
      actions: [
        actions.onSuccessEmailOtpVerification,
        actions.setIsGetEmailOtpButtonLoadingToFalse
      ],
      target: states.updateFieldValuesState
    },
    [events.onSuccessGetSignUpOTP]: {
      actions: [
        actions.setIsGetOtpButtonLoadingToFalse,
        assign((context) => {
          return {
            isOtpSentToUser: true,
            fieldsAuthDetails: {
              ...context.fieldsAuthDetails,
              [context.currentAuthenticationField]: {
                isOtpSentToUser: true
              }
            }
          };
        })
      ]
    },
    [events.onErrorGetOTP]: {
      actions: [
        actions.setIsGetOtpButtonLoadingToFalse,
        actions.clearErrorMessageFromContext
      ]
    },
    [events.onSuccessOtpVerification]: [
      {
        actions: [
          actions.setStudentLoginDetails,
          actions.onSuccessPhoneNumberOtpVerification
        ],
        target: states.sendEventOnPhoneNumberVerifiedState
      }
    ],
    [events.updateCurrentSelectedSectionTabId]: {
      actions: [
        actions.updateCurrentSelectedSectionTabId,
        xstateActions.choose([
          {
            cond: (context) =>
              context.currentSelectedSubSectionId ===
              ApplicationSectionNameEnums.NIAT_BOOTCAMP_REGISTRATION_2026,
            actions: send({ type: events.getFormConfig })
          }
        ])
      ]
    },
    [events.navigateFromQualifiedPreview]: {
      actions: [actions.navigateFromQualifiedPreview]
    },
    [events.updateContextValues]: {
      actions: [actions.updateValueOnContext]
    },
    [events.setEnrollmentCourseIdForPayment]: {
      actions: [actions.setEnrollmentCourseIdForPayment]
    },
    [events.updateSeatReservationFlow]: {
      actions: [actions.assignSeatReservationFlowToContext]
    },
    [events.updateFirstVisitDatetime]: {
      target: states.updateFirstVisitDatetimeState
    },
    [events.SET_USER_ENROLLMENT_STAGE]: {
      actions: [actions.setUserEnrollMentStage]
    },
    [events.updateFieldValues]: [
      {
        cond: (context, event) => {
          const { fieldsTemplateResponseConfig } = context;
          const {
            fieldDetail: { field }
          } = event;

          if (fieldsTemplateResponseConfig[field]) {
            return true;
          }
          return false;
        },
        target: states.updateTemplateResponseState
      },

      {
        actions: [actions.updateValueOnContext],
        target: states.updateFieldValuesState
      }
    ],
    [events.updateEnrollmentPaidStatus]: {
      actions: [
        actions.updateValueOnContext,
        assign((_context, event: any) => ({
          pendingEnrollmentPaidCrmPayload:
            event?.crmExtraInfoUpdatePayload ?? null
        }))
      ],
      target: states.updateTemplateResponseState
    },
    [events.initializeAuthMachineAndGetOtp]: {
      actions: [
        actions.updateValueOnContext,
        actions.setCurrentAuthenticationField,
        actions.spawnFieldSpecificAuthMachine
      ]
    },
    [events.submitApplicationForm]: {
      actions: [
        actions.updateValueOnContext,
        assign((context, event) => {
          return { isRescheduleExam: event.isRescheduleExam };
        })
      ],
      target: states.submitApplicationFormState
    },
    [events.updateSectionPercentage]: {
      actions: [
        assign((context, event) => {
          return {
            currentSectionToUpdateField: event.data.current_section,
            currentSectionToUpdateCompletionPercentage:
              context.currentSelectedSectionCompletionPercentage
          };
        }),
        actions.onSuccessUpdateFieldValues
      ]
    },
    [events.sectionProceedButtonClick]: [
      {
        cond: (context, event) => {
          if (event.shouldUpdateSectionFieldsValue) {
            return true;
          }
          return false;
        },
        actions: [
          assign((context, event) => {
            return {
              examFeeSectionNameEnum:
                event.nextSectionNameEnum ?? context.examFeeSectionNameEnum,
              requestObjectArrayForTemplateResponse:
                event.requestObjectArrayForTemplateResponse ?? []
            };
          })
        ],
        target: states.updateSectionFieldsValueState
      },
      {
        cond: (context, event) => {
          if (event.requestObjectArrayForTemplateResponse.length > 0) {
            return true;
          }
          return false;
        },
        actions: [
          assign((context, event) => {
            return {
              examFeeSectionNameEnum:
                event.nextSectionNameEnum ?? context.examFeeSectionNameEnum,
              requestObjectArrayForTemplateResponse:
                event.requestObjectArrayForTemplateResponse ?? []
            };
          })
        ],
        target: states.updateMultipleTemplateResponseState
      },
      {
        actions: [
          assign((context, event) => {
            return {
              examFeeSectionNameEnum:
                event.nextSectionNameEnum ?? context.examFeeSectionNameEnum
            };
          })
        ],
        target: states.updateApplicationDetailsState
      }
    ],
    [events.initializeProductsMachine]: {
      actions: [actions.initializeProductsMachine]
    },
    [events.getBootcampRegistrationFormSubmission]: {
      target: states.getBootcampRegistrationFormSubmissionState
    },
    [events.getFormConfig]: {
      target: states.getFormConfigState
    },
    [events.setShouldShowSuccessPage]: {
      actions: [
        assign((context, event) => {
          return { shouldShowSuccessPage: event.value };
        })
      ]
    },
    [events.resetAuthMachine]: {
      actions: [
        assign((context: NiatApplicationMachineContextType, event: any) => {
          const { fieldName } = event as any;

          return {
            fieldsAuthMachineRefDetails: {
              ...context.fieldsAuthMachineRefDetails,
              [fieldName]: null
            },
            isOtpSentToUser: false
          };
        })
      ]
    },
    [events.updateApplicationDetails]: {
      target: states.updateApplicationDetailsState
    },
    [events.recordDiscountInteraction]: {
      target: states.recordDiscountInteractionState
    },
    [events.setDiscountPopupVisibility]: {
      actions: [actions.setDiscountPopupVisibility]
    },
    [events.setHasRecordedCopyInteraction]: {
      actions: [actions.setHasRecordedCopyInteraction]
    },
    [events.updateTimerBannerVisibility]: {
      actions: [actions.updateTimerBannerVisibility]
    },
    [events.couponTimerExpired]: {
      actions: [actions.onCouponTimerExpire]
    },
    [events.updateTextInBanner]: {
      actions: [actions.updateTextInBanner]
    },
    [events.handleGracePeriodContinue]: {
      target: states.updateCouponExpiryState
    },
    [events.updateShouldDisableAllSectionsTabs]: {
      actions: [
        assign((context, event) => {
          return { shouldDisableAllSectionsTabs: event.value };
        })
      ]
    },
    [events.updateCurrentSectionType]: {
      actions: [
        assign((context, event) => {
          return { currentSectionType: event.sectionType };
        })
      ]
    },
    [events.logCloudWatchEvent]: {
      actions: [actions.logCloudWatchEvent]
    },
    [events.resetCouponCode]: {
      actions: [
        assign({
          couponCode: ''
        })
      ]
    },
    [events.setShowCongratulationsModal]: {
      actions: [
        assign((_context, event) => ({
          showCongratulationsModal: event.value
        }))
      ]
    },
    [events.getTopinExamAuthCode]: {
      actions: [
        assign((context, event) => {
          return {
            topinExamRedirection: event.topinExamRedirection,
            isGetTopinExamAuthCodeLoading: true
          };
        })
      ],
      target: states.getTopinExamAuthCodeState
    },
    [events.getAvailableProgramInstitutes]: {
      cond: (context: NiatApplicationMachineContextType) => {
        return (
          context.graphQlApiStatus?.getUserOrdersDetailsApiStatus !== LOADING
        );
      },
      target: states.getAvailableProgramInstitutesState
    },
    [events.createUserProgramInstitute]: {
      target: states.createUserProgramInstituteState
    },
    [events.getUserProgramInstituteCourses]: {
      target: states.getUserProgramInstituteCoursesState
    },
    [events.updateSeatReservationInstituteSectionDetails]: {
      actions: [actions.updateSeatReservationInstituteSectionDetails]
    }
  },
  states: {
    //This state is used to fetch user profile details
    [states.fetchUserProfileDetailsGraphQlState]: {
      invoke: {
        src: services.getUserProfileDetailGraphQL,
        onDone: [
          {
            //This condition will be checked when this state is triggered from logoutParentState
            //This is done to check if the user is logged in as parent and has an application
            cond: (context, event) => {
              if (context?.userApplicationDetails?.isParentLoggedIn) {
                return true;
              } else return false;
            },
            actions: [actions.onSuccessGetGraphQLProfileDetails],
            target: states.userApplicationDetailSectionsState
          },
          {
            actions: [actions.onSuccessGetGraphQLProfileDetails],
            target: states.getUserApplicationDetailState
          }
        ],
        onError: {
          actions: [actions.onFailGetGraphQLProfileDetails]
        }
      },
      on: {
        [events.doSelfTransition]: {
          target: states.fetchUserProfileDetailsGraphQlState
        }
      }
    },
    [states.getUserApplicationDetailState]: {
      invoke: {
        src: services.getUserApplicationDetails,
        onDone: [
          {
            cond: guards.checkIfParentLoggedInAndStudentApplicationNotExit,
            actions: [
              actions.onSuccessGettingUserApplicationDetails,
              assign((context, event) => {
                return {
                  loggedInUserRole: 'PARENT'
                };
              })
            ],
            target: states.userApplicationDetailSectionsState
          },
          {
            actions: [
              actions.onSuccessGettingUserApplicationDetails,
              actions.updateLoggedInUserRole
            ],
            cond: guards.checkIfParentLoggedIn,
            target: states.getStudentAuthDetailsState
          },
          {
            actions: [actions.onSuccessGettingUserApplicationDetails],
            cond: guards.isLoggedInUserRoleParent,
            target: states.linkUserApplicationState
          },
          {
            actions: [
              actions.onSuccessGettingUserApplicationDetails,
              actions.updateLoggedInUserRole
            ],
            target: states.userApplicationDetailSectionsState
          }
        ],
        onError: [
          {
            cond: guards.isUserApplicationNotExistsAndLoggedInUserRoleParent,
            actions: [
              assign((context) => {
                return {
                  graphQlApiStatus: {
                    ...context.graphQlApiStatus,
                    getUserApplicationDetailsAPIStatus: SUCCESS
                  },
                  isUserApplicationNotExit: true
                };
              })
            ],
            target: states.linkUserApplicationState
          },
          {
            cond: guards.isUserApplicationNotExists,
            actions: [
              assign((context) => {
                return {
                  graphQlApiStatus: {
                    ...context.graphQlApiStatus,
                    getUserApplicationDetailsAPIStatus: SUCCESS
                  }
                };
              }),
              actions.evaluateUserProfileDetailsFlow
            ],
            target: states.goToCreateApplicationState
          },
          {
            actions: [
              assign((context) => {
                return {
                  graphQlApiStatus: {
                    ...context.graphQlApiStatus,
                    getUserApplicationDetailsAPIStatus: ERROR
                  }
                };
              })
            ]
          }
        ]
      },
      on: {
        [events.doSelfTransition]: {
          target: states.getUserApplicationDetailState
        }
      }
    },
    //Application will be automatically created when a new user logged in and has no application
    [states.goToCreateApplicationState]: {
      entry: [actions.setIsProceedButtonLoadingToTrue],
      invoke: {
        src: services.createUserApplication,
        onDone: [
          {
            actions: [
              actions.onSuccessCreateUserApplication,
              actions.setIsProceedButtonLoadingToFalse
            ],
            target: states.userApplicationDetailSectionsState
          }
        ],
        onError: {
          actions: [
            actions.setIsProceedButtonLoadingToFalse,
            actions.assignErrorMessageToContext
          ]
        }
      }
    },

    [states.userApplicationDetailSectionsState]: {
      tags: ['userApplicationDetailSectionsState'],
      invoke: {
        src: services.getUserApplicationSectionsDetails,
        onDone: {
          actions: [
            actions.onSuccessGettingUserApplicationSectionsDetails,
            actions.updateLoggedInUserRole
          ],
          target: states.getInitialValueOfAllFieldsState
        },
        onError: {}
      }
    },

    [states.getInitialValueOfAllFieldsState]: {
      invoke: {
        src: services.getInitialValueOfAllFields,
        onDone: {
          actions: [actions.onSuccessGetInitialValueOfAllFields],
          target: states.sendEventForPageLandAndUpdateUnleashContextParallelState
        },
        onError: {
          actions: [
            actions.onFailGetInitialValueOfAllFields,
            actions.assignErrorMessageToContext
          ]
        }
      }
    },

    [states.sendEventForPageLandAndUpdateUnleashContextParallelState]: {
      type: 'parallel',
      states: {
        sendEventOnPageLand: {
          initial: 'invoking',
          states: {
            invoking: {
              invoke: {
                src: services.sendEventOnPageLand,
                onDone: { target: 'done' },
                onError: { target: 'done' }
              }
            },
            done: { type: 'final' }
          }
        },
      },
      onDone: {
        target: states.getTemplateResponseState
      }
    },

    [states.getTemplateResponseState]: {
      invoke: {
        src: services.getTemplateResponse,
        onDone: [
          {
            cond: (context, event) => {
              const isLinked =
                context.userApplicationDetails?.isStudentApplicationExist ===
                true &&
                context.userApplicationDetails?.isParentApplicationExist ===
                true;
              const whoIsFillingFieldId =
                context.fieldsTemplateResponseConfig['WHO_IS_FILLING']
                  ?.field_id;
              const whoIsFillingResponse = event.data?.field_responses?.find(
                (r: any) => r.field_id === whoIsFillingFieldId
              );
              return isLinked && whoIsFillingResponse?.field_value === 'PARENT';
            },
            actions: [actions.onSuccessGettingTemplateResponse],
            target: states.recoverStudentDetailsSyncState
          },
          {
            actions: [actions.onSuccessGettingTemplateResponse],
            target: states.updateDefaultWhoAmIFieldState
          }
        ],
        onError: [
          {
            actions: [
              actions.onSuccessGettingTemplateResponse,
              actions.onErrorGettingTemplateResponse
            ],
            target: states.updateDefaultWhoAmIFieldState
          }
        ]
      }
    },

    [states.recoverStudentDetailsSyncState]: {
      invoke: {
        src: services.recoverStudentDetailsSync,
        onDone: {
          actions: [actions.onSuccessRecoverStudentDetailsSync]
        },
        onError: {
          actions: [actions.onErrorRecoverStudentDetailsSync],
          target: states.updateDefaultWhoAmIFieldState
        }
      }
    },

    [states.updateDefaultWhoAmIFieldState]: {
      invoke: {
        src: services.updateDefaultWhoAmIField,
        onDone: [
          {
            target: states.updateUserProfileDetailsFlowFieldState,
            actions: [
              assign((context, event) => {
                const whoAmValue = context.initialValues['WHO_IS_FILLING'];
                if (whoAmValue === '') {
                  return {
                    initialValues: {
                      ...context.initialValues,
                      WHO_IS_FILLING: 'STUDENT'
                    },
                    examFeeSectionNameEnum:
                      ApplicationSectionNameEnums.BOOK_ADMISSION_TEST_FEE
                  };
                } else {
                  return {
                    examFeeSectionNameEnum:
                      ApplicationSectionNameEnums.BOOK_ADMISSION_TEST_FEE
                  };
                }
              })
            ]
          }
        ],
        onError: {
          target: states.updateUserProfileDetailsFlowFieldState,
          actions: [
            assign(() => ({
              examFeeSectionNameEnum:
                ApplicationSectionNameEnums.BOOK_ADMISSION_TEST_FEE
            }))
          ]
        }
      }
    },

    [states.updateUserProfileDetailsFlowFieldState]: {
      invoke: {
        src: services.updateUserProfileDetailsFlowField,
        onDone: {
          target: states.getApplicationDiscountDetailsState,
          actions: [
            actions.onSuccessUpdateUserProfileDetailsFlowField,
            actions.clearShouldUpdateUserProfileDetailsFlowField
          ]
        },
        onError: {
          target: states.getApplicationDiscountDetailsState,
          actions: [actions.clearShouldUpdateUserProfileDetailsFlowField]
        }
      }
    },

    [states.getApplicationDiscountDetailsState]: {
      invoke: {
        src: services.getApplicationDiscountDetails,
        onDone: {
          actions: [
            actions.onSuccessGetApplicationDiscountDetails,
            xstateActions.choose([
              {
                cond: (context) =>
                  context.currentSelectedSubSectionId ===
                  ApplicationSectionNameEnums.NIAT_BOOTCAMP_REGISTRATION_2026,
                actions: send({ type: events.getFormConfig })
              }
            ])
          ]
        },
        onError: {
          actions: [
            assign(
              (): Partial<NiatApplicationMachineContextType> => ({
                discountDetails: null,
                isTimerDiscountPopupVisible: false,
                isCouponTimerBannerVisible: false,
                couponTimerBannerTextDetails: null,
                isGracePeriodPopupVisible: false,
                isDiscountPopupVisible: false
              })
            ),
            // The bootcamp registration form config fetch must be dispatched even
            // when the discount API fails (e.g. USER_HAS_NO_ACCESS_TO_COUPON 400).
            // Users who land directly on the bootcamp registration sub-section have
            // no other init-time trigger for GET_FORM_CONFIG, so without this the
            // form config stays IDLE and the section is stuck on the loader.
            xstateActions.choose([
              {
                cond: (context) =>
                  context.currentSelectedSubSectionId ===
                  ApplicationSectionNameEnums.NIAT_BOOTCAMP_REGISTRATION_2026,
                actions: send({ type: events.getFormConfig })
              }
            ])
          ]
        }
      }
    },

    [states.recordDiscountInteractionState]: {
      invoke: {
        src: services.recordDiscountInteraction,
        onDone: {
          actions: [actions.onSuccessRecordDiscountInteraction]
        },
        onError: {
          actions: [
            assign(
              (): Partial<NiatApplicationMachineContextType> => ({
                isDiscountPopupVisible: false,
                isTimerDiscountPopupVisible: false,
                isCouponTimerBannerVisible: false,
                couponTimerBannerTextDetails: null,
                isGracePeriodPopupVisible: false
              })
            )
          ]
        }
      }
    },

    [states.updateCouponExpiryState]: {
      entry: [actions.setUpdateCouponExpiryLoading],
      invoke: {
        src: services.updateCouponExpiry,
        onDone: {
          actions: [actions.onSuccessUpdateCouponExpiry]
        },
        onError: {
          actions: [actions.onErrorUpdateCouponExpiry]
        }
      }
    },

    [states.updateApplicationDetailsState]: {
      entry: [actions.setIsProceedButtonLoadingToTrue],
      invoke: {
        src: services.updateUserApplication,
        onDone: [
          {
            cond: (context, event) => {
              if (context.initialValues['WHO_IS_FILLING'] === 'PARENT') {
                return true;
              }
              return false;
            },
            actions: [actions.onSuccessUpdateUserApplication],
            target: states.getStudentProfileDetailState
          },
          {
            actions: [
              actions.onSuccessUpdateUserApplication,
              actions.setIsProceedButtonLoadingToFalse,
              actions.goToApplicationSectionPage
            ]
          }
        ],
        onError: {
          actions: [
            actions.setIsProceedButtonLoadingToFalse,
            actions.assignErrorMessageToContext
          ]
        }
      }
    },
    [states.getStudentProfileDetailState]: {
      invoke: {
        src: services.getStudentProfileDetailWithAccessToken,
        onDone: [
          {
            actions: [actions.onSuccessGetStudentProfileDetail],
            target: states.getUserApplicationDetailState
          }
        ],
        onError: {
          actions: [
            actions.setIsProceedButtonLoadingToFalse,
            actions.assignErrorMessageToContext
          ]
        }
      }
    },

    [states.linkUserApplicationState]: {
      invoke: {
        src: services.linkUserApplication,
        onDone: [
          {
            target: states.logoutParentState
          }
        ],
        onError: {
          actions: [
            actions.setIsProceedButtonLoadingToFalse,
            actions.assignErrorMessageToContext
          ]
        }
      }
    },

    [states.logoutParentState]: {
      invoke: {
        src: services.logOutParent,
        onDone: [
          {
            actions: [actions.onSuccessLogOutParent],
            cond: (context) => {
              const { userApplicationDetails } = context;
              return userApplicationDetails.isParentLoggedIn;
            },
            target: states.fetchUserProfileDetailsGraphQlState
          },
          {
            actions: [actions.onSuccessLogOutParent],
            target: states.updateStudentDetailsState
          }
        ],
        onError: {
          actions: [
            actions.setIsProceedButtonLoadingToFalse,
            actions.assignErrorMessageToContext
          ]
        }
      }
    },

    [states.updateStudentDetailsState]: {
      invoke: {
        src: services.updateStudentDetails,
        onDone: {
          actions: [actions.onSuccessUpdateStudentDetails],
          target: states.updateWhoIsFillingState
        },
        onError: {
          actions: [
            actions.setIsProceedButtonLoadingToFalse,
            actions.assignErrorMessageToContext
          ]
        }
      }
    },

    [states.updateWhoIsFillingState]: {
      invoke: {
        src: services.updateWhoIsFilling,
        onDone: [
          {
            cond: (context, event) => {
              return context.initialValues['WHO_IS_FILLING'] !== '';
            },
            actions: [actions.setIsProceedButtonLoadingToFalse],
            target: states.goToApplicationSectionPageState
          }
        ],
        onError: {
          actions: [
            actions.setIsProceedButtonLoadingToFalse,
            actions.assignErrorMessageToContext
          ],
          target: states.goToApplicationSectionPageState
        }
      }
    },

    [states.goToApplicationSectionPageState]: {
      entry: actions.goToApplicationSectionPage
    },

    [states.getFormConfigState]: {
      entry: [
        assign((context) => {
          const { currentSelectedSectionTabId } = context;
          let shouldShowSuccessPage = false;
          //NOTE: Recheck this logic
          if (
            currentSelectedSectionTabId ===
            ApplicationSectionNameEnums.NAT_SLOT_SELECTION
          ) {
            shouldShowSuccessPage = true;
          }

          return {
            graphQlApiStatus: {
              ...context.graphQlApiStatus,
              getInitialFormConfigAPIStatus: LOADING
            },
            shouldShowSuccessPage: shouldShowSuccessPage
          };
        })
      ],
      invoke: {
        src: services.getFormConfig,
        onDone: {
          actions: [
            actions.onSuccessGetFormConfig,
            assign((context) => {
              return {
                graphQlApiStatus: {
                  ...context.graphQlApiStatus,
                  getInitialFormConfigAPIStatus: SUCCESS
                }
              };
            })
          ],
          target: states.getFormReferralCodeState
        },
        onError: {
          actions: [
            assign((context, event) => {
              const errorMessage = getAPIErrorResponse(event?.data);
              return {
                graphQlApiStatus: {
                  ...context.graphQlApiStatus,
                  getInitialFormConfigAPIStatus: ERROR
                },
                errorMessages: {
                  ...context.errorMessages,
                  getInitialFormConfigErrorMessage: errorMessage
                }
              };
            })
          ]
        }
      }
    },

    [states.getFormReferralCodeState]: {
      invoke: {
        src: services.getFormReferralCode,
        onDone: [
          {
            cond: guards.shouldTrackFormReferralAttempt,
            actions: [actions.onSuccessGetFormReferralCode],
            target: states.trackFormReferralAttemptState
          },
          {
            actions: [actions.onSuccessGetFormReferralCode],
            target: states.getIbFormSubmissionDetailsState
          }
        ],
        onError: {
          target: states.getIbFormSubmissionDetailsState
        }
      }
    },

    [states.trackFormReferralAttemptState]: {
      invoke: {
        src: services.trackFormReferralAttempt,
        onDone: {
          target: states.getIbFormSubmissionDetailsState
        },
        onError: {
          actions: [actions.onErrorTrackFormReferralAttemptLogEntry],
          target: states.getIbFormSubmissionDetailsState
        }
      }
    },

    [states.getIbFormSubmissionDetailsState]: {
      entry: [
        assign((context) => {
          return {
            graphQlApiStatus: {
              ...context.graphQlApiStatus,
              getIbFormSubmissionDetailsAPIStatus: LOADING
            }
          };
        })
      ],

      invoke: {
        src: services.getIbFormSubmissionDetails,
        onDone: {
          actions: [actions.onSuccessGetIbFormSubmissionDetails]
        },
        onError: {
          actions: [
            actions.onErrorGetIbFormSubmissionDetails,
            actions.assignErrorMessageToContext
          ]
        }
      }
    },

    [states.getBootcampRegistrationFormSubmissionState]: {
      invoke: {
        src: services.getBootcampRegistrationFormSubmission,
        onDone: {
          actions: [actions.onSuccessGetBootcampRegistrationFormSubmission]
        },
        onError: {
          actions: []
        }
      }
    },

    [states.updateUserProfileDetailGraphQlState]: {
      invoke: {
        src: services.updateUserProfileDetailsGraphQL,
        onDone: {
          actions: [actions.updateUserGraphQlProfileDetailsInContext]
        }
      }
    },

    [states.updateUserIntermediateDetailGraphQlState]: {
      invoke: {
        src: services.updateUserIntermediateDetailGraphQL
      }
    },

    [states.updateUserDegreeDetailsGraphQlState]: {
      invoke: {
        src: services.updateUserDegreeDetailsGraphQL
      }
    },

    [states.updateFieldValuesState]: {
      onEntry: [
        actions.onEntryUpdateFieldValues,
        actions.setIsFieldUpdateInProgressToTrue
      ],
      invoke: {
        src: services.updateFieldValues,
        onDone: [
          {
            actions: [
              actions.onSuccessUpdateFieldValues,
              actions.setIsFieldUpdateInProgressToFalse
            ]
          }
        ],
        onError: {
          actions: [
            actions.onFailUpdateFieldValues,
            actions.setIsFieldUpdateInProgressToFalse
          ]
        }
      }
    },

    [states.updateTemplateResponseState]: {
      entry: [
        actions.updateValueOnContext,
        assign((context: NiatApplicationMachineContextType, event: any) => {
          const {
            currentSelectedSectionTabId,
            currentSelectedSubSectionId,
            currentSelectedSectionCompletionPercentage,
            initialValues
          } = context;
          const currentSelectedSection = currentSelectedSubSectionId
            ? currentSelectedSubSectionId
            : currentSelectedSectionTabId;

          if (event.values['WHO_IS_FILLING']) {
            initialValues['WHO_IS_FILLING'] = event.values['WHO_IS_FILLING'];
          }

          return {
            currentSectionToUpdateField: currentSelectedSection,
            currentSectionToUpdateCompletionPercentage:
              currentSelectedSectionCompletionPercentage,
            isFieldUpdateInProgress: true,
            initialValues: initialValues
          };
        })
      ],
      invoke: {
        src: services.updateTemplateResponse,
        onDone: [
          {
            cond: (context: NiatApplicationMachineContextType) => {
              return Boolean(context.pendingEnrollmentPaidCrmPayload?.orderId);
            },
            actions: [
              actions.onSuccessUpdateFieldValues,
              actions.setIsFieldUpdateInProgressToFalse
            ],
            target: states.updateEnrollmentPaidStatusState
          },
          {
            actions: [
              actions.onSuccessUpdateFieldValues,
              actions.setIsFieldUpdateInProgressToFalse
            ]
          }
        ],
        onError: {
          actions: [
            actions.onErrorUpdatingTemplateResponse,
            actions.setIsFieldUpdateInProgressToFalse
          ]
        }
      }
    },
    [states.updateEnrollmentPaidStatusState]: {
      invoke: {
        src: services.updateEnrollmentPaidStatus,
        onDone: {
          actions: [
            assign(() => ({
              pendingEnrollmentPaidCrmPayload: null
            }))
          ]
        },
        onError: {
          actions: [
            assign(() => ({
              pendingEnrollmentPaidCrmPayload: null
            }))
          ]
        }
      }
    },

    [states.submitApplicationFormState]: {
      entry: [actions.setIsProceedButtonLoadingToTrue],
      invoke: {
        src: services.submitApplicationForm,
        onDone: [
          {
            cond: (context: NiatApplicationMachineContextType) =>
              context.currentSelectedSubSectionId ===
              ApplicationSectionNameEnums.NIAT_BOOTCAMP_REGISTRATION_2026 ||
              context.currentSelectedSectionTabId ===
              ApplicationSectionNameEnums.NIAT_BOOTCAMP_REGISTRATION_2026,
            actions: [
              actions.onSuccessSubmitApplicationForm,
              actions.setIsProceedButtonLoadingToFalse,
              send((context: NiatApplicationMachineContextType) => ({
                type: 'UPDATE_SECTION_PERCENTAGE',
                data: {
                  current_section:
                    context.currentSelectedSubSectionId ||
                    ApplicationSectionNameEnums.NIAT_BOOTCAMP_REGISTRATION_2026,
                  completion_percentage: 100
                }
              }))
            ]
          },
          {
            actions: [actions.onSuccessSubmitApplicationForm],
            target: states.sendEventOnApplicationFormSubmitState
          }
        ],
        onError: {
          actions: [
            actions.setIsProceedButtonLoadingToFalse,
            actions.onErrorSubmitApplicationForm
          ]
        }
      }
    },

    [states.sendEventOnApplicationFormSubmitState]: {
      invoke: {
        src: services.sendEventOnApplicationSubmit,
        onDone: {
          actions: [
            actions.setIsProceedButtonLoadingToFalse,

            send((context: NiatApplicationMachineContextType) => {
              return {
                type: 'UPDATE_SECTION_PERCENTAGE',
                data: {
                  current_section: context.currentSelectedSubSectionId,
                  completion_percentage: context.isRescheduleExam ? 50 : 100
                }
              };
            }),
            send((context: NiatApplicationMachineContextType) => {
              // Find NAT_SLOT_SELECTION section and TOPIN_EXAM subsection
              const natSlotSelectionSection =
                context.sectionDetailsInfoTabsConfig?.find(
                  (section: any) =>
                    section.id ===
                    ApplicationSectionNameEnums.NAT_SLOT_SELECTION
                );
              const topinExamSubSection =
                natSlotSelectionSection?.subSection?.find(
                  (subSection: any) =>
                    subSection.id === ApplicationSectionNameEnums.TOPIN_EXAM
                );
              const retakeTopinExamSubSection =
                natSlotSelectionSection?.subSection?.find(
                  (subSection: any) =>
                    subSection.id ===
                    ApplicationSectionNameEnums.RETAKE_TOPIN_EXAM
                );

              // Use case 1: Rescheduling - stay on NAT_SLOT_SELECTION, parent = 50%
              if (context.isRescheduleExam) {
                return {
                  type: 'UPDATE_CURRENT_SELECTED_SECTION_TAB_ID',
                  tabId: ApplicationSectionNameEnums.NAT_SLOT_SELECTION
                };
              }

              // Use case 2 & 3: Not rescheduling - navigate to TOPIN_EXAM if it exists
              if (topinExamSubSection) {
                return {
                  type: 'UPDATE_CURRENT_SELECTED_SECTION_TAB_ID',
                  tabId: ApplicationSectionNameEnums.TOPIN_EXAM
                };
              }

              if (retakeTopinExamSubSection) {
                return {
                  type: 'UPDATE_CURRENT_SELECTED_SECTION_TAB_ID',
                  tabId: ApplicationSectionNameEnums.RETAKE_TOPIN_EXAM
                };
              }

              // Fallback: stay on NAT_SLOT_SELECTION
              return {
                type: 'UPDATE_CURRENT_SELECTED_SECTION_TAB_ID',
                tabId: ApplicationSectionNameEnums.NAT_SLOT_SELECTION
              };
            })
            // Note: Parent section completion percentage is automatically recalculated
            // from subsections in onSuccessUpdateFieldValues action, so we don't need
            // to explicitly set it here. The first UPDATE_SECTION_PERCENTAGE event
            // for the subsection will trigger the parent recalculation.
          ]
        },
        onError: {
          actions: [
            actions.setIsProceedButtonLoadingToFalse
            // On error, don't update completion percentages or navigate
            // Keep user on current section so they can see error and retry
            // The error message will be displayed via actions.onErrorSubmitApplicationForm
          ]
        }
      }
    },

    [states.getStudentAuthDetailsState]: {
      invoke: {
        src: services.getStudentAuthDetails,
        onDone: {
          actions: [actions.onSuccessGetStudentAuthDetails],
          target: states.logoutParentState
        },
        onError: {
          actions: [actions.assignErrorMessageToContext]
        }
      }
    },
    [states.updateSectionFieldsValueState]: {
      entry: [
        assign((context) => {
          return {
            graphQlApiStatus: {
              ...context.graphQlApiStatus,
              updateMultipleGraphQlMutationsStatus: LOADING
            }
          };
        })
      ],
      invoke: {
        src: services.updateSectionFieldsValue,
        onDone: [
          {
            cond: guards.isGraphQLApiHasFailureResponse,
            actions: [
              actions.updateUserGraphQlProfileDetailsInContext,
              actions.onErrorUpdateSectionFieldValues,
              assign((context) => {
                return {
                  graphQlApiStatus: {
                    ...context.graphQlApiStatus,
                    updateMultipleGraphQlMutationsStatus: ERROR
                  }
                };
              })
            ]
          },
          {
            cond: (context, event) => {
              if (context.requestObjectArrayForTemplateResponse.length > 0) {
                return true;
              }
              return false;
            },
            target: states.updateMultipleTemplateResponseState
          },
          {
            cond: (context, event) => {
              if (context.loggedInUserRole !== 'STUDENT') {
                return true;
              }
              const isDirectCampusVisited =
                context.initialValues?.IS_DIRECT_CAMPUS_VISITED === 'YES';
              const isFirstTimePersonalDetailsSaveAndBook =
                context.currentSelectedSectionTabId ===
                ApplicationSectionNameEnums.PERSONAL_DETAILS &&
                context.currentSelectedSectionCompletionPercentage < 100;
              return (
                isFirstTimePersonalDetailsSaveAndBook && isDirectCampusVisited
              );
            },
            target: states.updateApplicationDetailsState,
            actions: [
              actions.onSuccessUpdateFieldValues,
              assign((context) => {
                return {
                  graphQlApiStatus: {
                    ...context.graphQlApiStatus,
                    updateMultipleGraphQlMutationsStatus: SUCCESS
                  }
                };
              })
            ]
          },
          {
            actions: [
              actions.onSuccessUpdateFieldValues,
              assign((context) => {
                return {
                  graphQlApiStatus: {
                    ...context.graphQlApiStatus,
                    updateMultipleGraphQlMutationsStatus: SUCCESS
                  }
                };
              }),
              send((context: NiatApplicationMachineContextType) => {
                return {
                  type: 'UPDATE_CURRENT_SELECTED_SECTION_TAB_ID',
                  //NOTE: Write the next section name enum
                  tabId: context.examFeeSectionNameEnum
                };
              })
            ]
          }
        ],
        onError: [
          {
            actions: [
              actions.onErrorUpdateSectionFieldValues,
              assign((context) => {
                return {
                  graphQlApiStatus: {
                    ...context.graphQlApiStatus,
                    updateMultipleGraphQlMutationsStatus: ERROR
                  }
                };
              })
            ]
          }
        ]
      }
    },
    [states.updateMultipleTemplateResponseState]: {
      entry: [
        assign((context) => {
          return {
            graphQlApiStatus: {
              ...context.graphQlApiStatus,
              updateMultipleGraphQlMutationsStatus: LOADING
            }
          };
        })
      ],
      invoke: {
        src: services.updateMultipleTemplateResponse,
        onDone: [
          {
            cond: (context, event) => {
              if (!context.loggedInUserRole) {
                return true;
              }
              const isDirectCampusVisited =
                context.initialValues?.IS_DIRECT_CAMPUS_VISITED === 'YES';
              const isFirstTimePersonalDetailsSaveAndBook =
                context.currentSelectedSectionTabId ===
                ApplicationSectionNameEnums.PERSONAL_DETAILS &&
                context.currentSelectedSectionCompletionPercentage < 100;
              return (
                isFirstTimePersonalDetailsSaveAndBook && isDirectCampusVisited
              );
            },
            target: states.updateApplicationDetailsState,
            actions: [
              actions.onSuccessUpdateFieldValues,
              assign((context) => {
                return {
                  graphQlApiStatus: {
                    ...context.graphQlApiStatus,
                    updateMultipleGraphQlMutationsStatus: SUCCESS
                  }
                };
              })
            ]
          },
          {
            actions: [
              actions.onSuccessUpdateFieldValues,
              assign((context) => {
                return {
                  graphQlApiStatus: {
                    ...context.graphQlApiStatus,
                    updateMultipleGraphQlMutationsStatus: SUCCESS
                  }
                };
              }),
              send((context: NiatApplicationMachineContextType) => {
                return {
                  type: 'UPDATE_CURRENT_SELECTED_SECTION_TAB_ID',
                  tabId: context.examFeeSectionNameEnum
                };
              })
            ]
          }
        ],
        onError: {
          actions: [
            actions.onErrorUpdateSectionFieldValues,
            assign((context) => {
              return {
                graphQlApiStatus: {
                  ...context.graphQlApiStatus,
                  updateMultipleGraphQlMutationsStatus: ERROR
                }
              };
            })
          ]
        }
      }
    },
    [states.sendEventOnPhoneNumberVerifiedState]: {
      invoke: {
        src: services.sendEventOnPhoneNumberVerified
      }
    },
    [states.updateFirstVisitDatetimeState]: {
      invoke: {
        src: services.updateFirstVisitDatetime,
        onDone: [
          {
            actions: [actions.onSuccessUpdateFirstVisitDatetime]
          }
        ],
        onError: {}
      }
    },
    [states.getTopinExamAuthCodeState]: {
      invoke: {
        src: services.getTopinExamAuthCode,
        onDone: {
          actions: [actions.onSuccessGetTopinExamAuthCode]
        },
        onError: {
          actions: [
            actions.onErrorGetTopinExamAuthCode,
            actions.setIsGetTopinExamAuthCodeLoadingToFalse
          ]
        }
      }
    },
    [states.getAvailableProgramInstitutesState]: {
      entry: [actions.setAvailableProgramInstitutesLoading],
      invoke: {
        src: services.getAvailableProgramInstitutes,
        onDone: {
          actions: [actions.onSuccessGetAvailableProgramInstitutes]
        },
        onError: {
          actions: [actions.onErrorGetAvailableProgramInstitutes]
        }
      }
    },
    [states.createUserProgramInstituteState]: {
      entry: [
        assign((context) => {
          return {
            graphQlApiStatus: {
              ...context.graphQlApiStatus,
              updateInstituteAndAccommodationApiStatus: LOADING
            }
          };
        })
      ],
      invoke: {
        src: services.createUserProgramInstitute,
        onDone: [
          {
            actions: [actions.onSuccessCreateUserProgramInstitute]
          }
        ],
        onError: {
          actions: [actions.onErrorCreateUserProgramInstitute]
        }
      }
    },
    [states.getUserProgramInstituteCoursesState]: {
      entry: [actions.setUserProgramInstituteCoursesLoading],
      invoke: {
        src: services.getUserProgramInstituteCourses,
        onDone: [
          {
            actions: [actions.onSuccessGetUserProgramInstituteCourses],
            cond: guards.doesUserProgramInstituteCourseRowHaveInstituteId,
            target: states.getSelectedInstituteCoursesState
          },
          {
            actions: [
              actions.onSuccessGetUserProgramInstituteCourses,
              send((context: NiatApplicationMachineContextType) => {
                return {
                  type: 'SET_USER_ENROLLMENT_STAGE',
                  userEnrollMentStage: 'YET_TO_DECIDE'
                };
              })
            ],
            target: states.getUserOrdersDetailsState
          }
        ],
        onError: {
          actions: [actions.onErrorGetUserProgramInstituteCourses]
        }
      }
    },
    [states.getUserOrdersDetailsState]: {
      entry: [actions.setUserOrdersDetailsLoading],
      invoke: {
        src: services.getUserOrdersDetails,
        onDone: [
          {
            actions: [actions.onSuccessGetUserOrdersDetails],
            cond: guards.shouldGetAvailableProgramInstitutesAfterUserOrders,
            target: states.getAvailableProgramInstitutesState
          },
          {
            actions: [actions.onSuccessGetUserOrdersDetails]
          }
        ],
        onError: {
          actions: [actions.onErrorGetUserOrdersDetails]
        }
      }
    },
    [states.getSelectedInstituteCoursesState]: {
      invoke: {
        src: services.getSelectedInstituteCourses,
        onDone: [
          {
            actions: [actions.onSuccessGetSelectedInstituteCourses],
            cond: guards.doesSelectedInstituteHaveCourses,
            target: states.decideEnrollmentFlowState
          },
          {
            actions: [
              send((context: NiatApplicationMachineContextType) => {
                return {
                  type: 'SET_USER_ENROLLMENT_STAGE',
                  userEnrollMentStage: 'NO_COURSES_AVAILABLE'
                };
              })
            ],
            target: states.getUserOrdersDetailsState
          }
        ],
        onError: {
          actions: [actions.onErrorGetSelectedInstituteCourses]
        }
      }
    },
    [states.decideEnrollmentFlowState]: {
      entry: [
        assign((context) => {
          return {
            userEnrollMentStage: 'COURSES_AVAILABLE'
          };
        })
      ],
      invoke: {
        src: services.getAvailableProgramInstitutes,
        onDone: {
          actions: [actions.onSuccessGetAvailableProgramInstitutes],
          target: states.getUserOrdersDetailsState
        },
        onError: {
          actions: [actions.onErrorGetAvailableProgramInstitutes]
        }
      }
    }
  }
});
